import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Box, Flex } from '@chakra-ui/react';
import { Text } from '@kula-ai/components';
import { Folder, LayersThree01, Send01 } from '@kula-ai/icons-core';
import type { AnonymizeCandidate } from './AnonymizeCandidateModal';

interface RecordItem {
  label: string;
  // Route to the job / project / flow; absent when the id is missing.
  to?: string;
}

interface RecordGroupProps {
  icon: ReactNode;
  label: string;
  items: RecordItem[];
  emptyText: string;
}

// One "Currently part of" row: a labelled icon, then the record names (linked to
// their job / project / flow) or an empty note.
const RecordGroup = ({ icon, label, items, emptyText }: RecordGroupProps) => (
  <Box>
    <Flex align="center" gap="8px">
      <Box as="span" display="inline-flex" color="secondary-700">
        {icon}
      </Box>
      <Text variant="p" color="heading">
        {label}
      </Text>
    </Flex>
    <Box pl="26px" mt="2px">
      {items.length ? (
        items.map((item, index) =>
          item.to ? (
            // New tab so following the link doesn't abandon the confirm dialog.
            <Link
              key={`${item.label}-${index}`}
              to={item.to}
              target="_blank"
              rel="noopener noreferrer"
            >
              {/* Reads as normal text; only the hover reveals it's a link. */}
              <Text
                variant="p"
                color="body"
                className="cursor-pointer hover:!text-primary-500 hover:underline"
              >
                {item.label}
              </Text>
            </Link>
          ) : (
            <Text key={`${item.label}-${index}`} variant="p" color="body">
              {item.label}
            </Text>
          )
        )
      ) : (
        <Text variant="p" color="muted">
          {emptyText}
        </Text>
      )}
    </Box>
  </Box>
);

// Map a raw record array to { label, to } items — label from `nameKey`, route
// from `buildTo`. Rows without a usable name are dropped.
const toRecords = (
  list: unknown,
  nameKey: string,
  buildTo: (item: Record<string, unknown>) => string | undefined
): RecordItem[] =>
  (Array.isArray(list) ? list : [])
    .map((raw) => {
      const item = (raw ?? {}) as Record<string, unknown>;
      const label = item[nameKey];
      if (typeof label !== 'string' || !label) return null;
      return { label, to: buildTo(item) };
    })
    .filter((v): v is RecordItem => v !== null);

// The jobs / projects / flows the candidate is currently on — shown (and linked)
// so the recruiter sees what stays linked (for reporting) after anonymizing.
export const AnonymizeRecordsList = ({
  candidate,
}: {
  candidate: AnonymizeCandidate;
}) => (
  <Flex flexDir="column" gap="16px">
    <RecordGroup
      icon={<LayersThree01 size={16} />}
      label="Jobs"
      items={toRecords(candidate.ats_applications, 'ats_job_name', (i) => {
        // Records carry `job_id` (older rows use `ats_job_id`); the job route
        // keys off it, with the application id as `appId` (CORE-5299).
        const jobId = i.ats_job_id || i.job_id;
        if (!jobId) return undefined;
        const appId = i.id ? `?tab=candidates&appId=${String(i.id)}` : '';
        return `/jobs/${String(jobId)}${appId}`;
      })}
      emptyText="No jobs"
    />
    <RecordGroup
      icon={<Folder size={16} />}
      label="Projects"
      items={toRecords(candidate.projects, 'name', (i) =>
        i.id ? `/projects/${String(i.id)}` : undefined
      )}
      emptyText="No projects"
    />
    <RecordGroup
      icon={<Send01 size={16} />}
      label="Flows"
      items={toRecords(candidate.outreaches, 'title', (i) =>
        i.id ? `/flows/${String(i.id)}/candidates` : undefined
      )}
      emptyText="No flows"
    />
  </Flex>
);
